/**
 * sessionDB.js — single source of truth for the logged-in user's session.
 *
 * Every part of the app (login, welcome/dashboard shell, the merged HRMS/ESS
 * modules, and the shared api clients) reads and writes the session through
 * this file only. Nothing else should touch `sessionStorage` directly.
 *
 * The full login response (see response.txt) is persisted as-is:
 *   { accessToken, refreshToken, tokenType, expiresIn, empobj, tenobj, enabledServices }
 *
 * Storing the whole payload (instead of a hand-picked subset) means the
 * access token, tenant code, employee id, and the dynamic `enabledServices`
 * tree all survive a page refresh — sessionStorage lives for the life of the
 * browser tab, which is exactly the "session DB" behaviour we want.
 */

const SESSION_KEY = 'wp_session';

/**
 * Keep the browser representation stable even if a gateway returns the
 * conventional snake_case token names.  A session written before this change
 * is also normalised as it is read, which is important on a page reload.
 */
function normalizeSession(value) {
  if (!value || typeof value !== 'object') return null;

  const payload = value.data && typeof value.data === 'object' &&
    !value.accessToken && !value.access_token
    ? value.data
    : value;

  return {
    ...payload,
    accessToken: payload.accessToken || payload.access_token || '',
    refreshToken: payload.refreshToken || payload.refresh_token || '',
  };
}

function readRaw() {
  try {
    // A remembered login is preferred when a browser is reopened. A normal
    // login remains tab-only by design.
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    return raw ? normalizeSession(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeRaw(obj) {
  try {
    const storage = obj.rememberMe === true ? localStorage : sessionStorage;
    const otherStorage = obj.rememberMe === true ? sessionStorage : localStorage;
    storage.setItem(SESSION_KEY, JSON.stringify(obj));
    otherStorage.removeItem(SESSION_KEY);
  } catch {
    // Browser storage can throw in private-browsing/quota-exceeded situations;
    // fail silently so the in-memory app state still works for this tab.
  }
  window.dispatchEvent(new Event('session-updated'));
  return obj;
}

/** Persist the full login response. Call this once, right after login. */
export function saveSession(loginResponseData) {
  const session = normalizeSession(loginResponseData);
  if (!session) return null;
  return writeRaw({
    ...session,
    accessTokenExpiresAt: session.expiresIn
      ? Date.now() + Number(session.expiresIn) * 1000
      : session.accessTokenExpiresAt,
  });
}

/** Merge a partial patch into the existing session (e.g. token refresh). */
export function updateSession(patch = {}) {
  const next = normalizeSession({ ...getSession(), ...patch });
  return writeRaw(next);
}

/** Always returns an object (never null) so callers can destructure freely. */
export function getSession() {
  return readRaw() || {};
}

export function hasSession() {
  return Boolean(readRaw()?.accessToken);
}

export function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* no-op */
  }
  window.dispatchEvent(new Event('session-updated'));
}

/* ── Derived convenience getters ──────────────────────────────────────── */
export function getAccessToken() {
  return getSession().accessToken || '';
}

export function getRefreshToken() {
  return getSession().refreshToken || '';
}

export function getEmpObj() {
  const session = getSession();
  // Older responses use `empobj`; accept the camel-cased backend variant too.
  return session.empobj || session.empObj || {};
}
export function getEmpPreferences() {
  const session = getSession();
  // Accept legacy preference fields and the current login-response field.
  const preferences = session.empPreferences ?? session.prefobj ?? session.prefObj ?? {};
  if (typeof preferences !== 'string') return preferences;

  try {
    const parsed = JSON.parse(preferences);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Update preferences in the browser session after the backend accepts them. */
export function updateEmpPreferences(preferences) {
  const session = getSession();
  const key = Object.prototype.hasOwnProperty.call(session, 'empPreferences')
    ? 'empPreferences'
    : Object.prototype.hasOwnProperty.call(session, 'prefobj')
      ? 'prefobj'
      : Object.prototype.hasOwnProperty.call(session, 'prefObj')
        ? 'prefObj'
        : 'empPreferences';
  const current = session[key];
  const storedValue = typeof current === 'string'
    ? JSON.stringify(preferences)
    : preferences;
  return updateSession({ [key]: storedValue });
}

/** Return only announcements with displayable text; null means no announcements. */
export function getTenantAnnouncements() {
  const announcements = getSession().tenAnnouncements;
  if (!Array.isArray(announcements)) return [];
  return announcements.filter(announcement => announcement && (
    (typeof announcement.announcementTitle === 'string' && announcement.announcementTitle.trim()) ||
    (typeof announcement.announcementDescription === 'string' && announcement.announcementDescription.trim())
  )).map(announcement => ({
    announcementTitle: typeof announcement.announcementTitle === 'string' ? announcement.announcementTitle : '',
    announcementDescription: typeof announcement.announcementDescription === 'string' ? announcement.announcementDescription : '',
  }));
}

export function getTenantObj() {
  return getSession().tenobj || {};
}

export function getEmpId() {
  return getEmpObj().emp_id ?? '';
}
export function getUiOption() {
  return getEmpPreferences().uiOption ?? '';
}

export function getTenantId() {
  return getTenantObj().tenantId ?? '';
}

/** Tenant code is what the HRMS/ESS gateway expects in X-Tenant-Code. */
export function getTenantCode() {
  return getTenantObj().tenantCode ?? '';
}

export function getCanonicalRoleName() {
  return getEmpObj().canonical_role_name || '';
}

/** The dynamic, per-user enabled-services tree returned at login. */

export function getEnabledServices() {
  const enabledServices = getSession().enabledServices;
  return enabledServices && typeof enabledServices === 'object' && !Array.isArray(enabledServices)
    ? enabledServices : {};
}

const sessionDB = {
  saveSession,
  updateSession,
  getSession,
  hasSession,
  clearSession,
  getAccessToken,
  getRefreshToken,
  getEmpObj,
  getEmpPreferences,
  updateEmpPreferences,
  getTenantObj,
  getTenantAnnouncements,
  getEmpId,
  getTenantId,
  getTenantCode,
  getUiOption,
  getCanonicalRoleName,
  getEnabledServices,
};

export default sessionDB;